/**
 * Gmail-style Email List
 * Clean, compact rows with checkbox, star, sender, subject, preview, date.
 */
import { useRef, useEffect } from 'react';

function formatDate(d) {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const isThisYear = date.getFullYear() === now.getFullYear();
  if (isThisYear) return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

function parseSender(address) {
  if (!address) return { name: '', email: '' };
  if (typeof address === 'object') return { name: address.name || '', email: address.email || '' };
  const match = address.match(/^(.+?)\s*<(.+?)>$/);
  if (match) return { name: match[1].trim(), email: match[2].trim() };
  return { name: address.split('@')[0], email: address };
}

function SenderAvatar({ name, email, size = 32 }) {
  const initial = (name || email || '?')[0].toUpperCase();
  const colors = [
    'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500',
    'bg-teal-500', 'bg-cyan-500', 'bg-blue-500', 'bg-indigo-500',
    'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500',
  ];
  const hash = (email || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const color = colors[hash % colors.length];

  return (
    <div className={`${color} rounded-full flex items-center justify-center flex-shrink-0 text-white font-medium`}
      style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {initial}
    </div>
  );
}

export default function EmailList({
  emails = [],
  loading,
  selectedIds = new Set(),
  onToggleSelect,
  onSelectEmail,
  onStar,
  activeId,
  folder,
  focusedIndex,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
}) {
  const handleScroll = (e) => {
    const el = e.currentTarget;
    // Near the bottom → pull the next page.
    if (hasMore && !loadingMore && el.scrollHeight - el.scrollTop - el.clientHeight < 400) {
      onLoadMore?.();
    }
  };

  // Keep the j/k-focused row visible.
  const focusedRef = useRef(null);
  useEffect(() => {
    focusedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex]);
  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500" />
      </div>
    );
  }

  if (emails.length === 0) {
    return null; // Parent handles empty state
  }

  return (
    <div className="flex-1 overflow-y-auto" onScroll={handleScroll}>
      {folder === 'trash' && (
        <div className="border-b border-gray-100 bg-amber-50 px-4 py-2 text-xs text-amber-700 flex items-center gap-2">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01" />
          </svg>
          Messages in Trash are auto-deleted after 30 days
        </div>
      )}
      {folder === 'spam' && (
        <div className="border-b border-gray-100 bg-red-50 px-4 py-2 text-xs text-red-600 flex items-center gap-2">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Messages identified as spam
        </div>
      )}

      {emails.map((email, index) => {
        const isSelected = selectedIds?.has?.(email.id);
        const isActive = activeId === email.id;
        const isFocused = focusedIndex === index;
        const isSent = email._type === 'sent' || email._type === 'draft' || folder === 'sent' || folder === 'drafts';
        const unread = !isSent && !email.is_read;

        let displayName;
        if (isSent) {
          // For sent mail, show recipient(s)
          const toArr = email.to_addresses || [];
          if (typeof toArr === 'string') {
            displayName = toArr;
          } else if (Array.isArray(toArr) && toArr.length > 0) {
            const first = toArr[0];
            const name = first?.name || first?.email || first;
            displayName = toArr.length > 1 ? `${name} +${toArr.length - 1}` : String(name);
          } else {
            displayName = email.to_address || '';
          }
        } else {
          const sender = parseSender(email.from_address || '');
          displayName = sender.name || sender.email;
        }
        const preview = email.body_preview || '';
        const subject = email.subject || '(no subject)';
        const hasAttachments = email.attachments && JSON.parse(typeof email.attachments === 'string' ? email.attachments : '[]').length > 0;

        return (
          <div
            key={email.id}
            ref={isFocused ? focusedRef : null}
            onClick={() => onSelectEmail?.(email)}
            className={`
              group flex items-center cursor-pointer border-b border-gray-100 transition-colors
              ${isActive ? 'bg-blue-50/80' : isFocused ? 'ring-2 ring-inset ring-blue-400 bg-blue-50/50' : unread ? 'bg-white' : 'bg-gray-50/30'}
              ${unread ? '' : 'opacity-90'}
              hover:shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)] hover:bg-gray-50
            `}
          >
            {/* Checkbox + Star */}
            <div className="flex items-center gap-1 pl-2 pr-1 py-2 flex-shrink-0">
              <input
                type="checkbox"
                checked={isSelected || false}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggleSelect?.(email.id)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 opacity-0 group-hover:opacity-100 checked:opacity-100 transition-opacity"
              />
              <button
                onClick={(e) => { e.stopPropagation(); onStar?.(email.id); }}
                className={`p-0.5 ${email.is_starred ? 'text-amber-400' : 'text-transparent group-hover:text-gray-300 hover:!text-amber-300'} transition-colors`}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill={email.is_starred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
                  <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </button>
            </div>

            {/* Sender */}
            <div className={`w-36 flex-shrink-0 truncate pr-2 py-2 text-[13px] ${unread ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
              {isSent && <span className="text-gray-400 mr-1">To:</span>}
              {displayName}
            </div>

            {/* Subject + Preview (Gmail-style single line) */}
            <div className="flex-1 min-w-0 flex items-baseline gap-1 py-2 pr-2">
              <span className={`truncate text-[13px] ${unread ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                {subject}
              </span>
              {preview && (
                <span className="truncate text-[13px] text-gray-400 font-normal">
                  &mdash; {preview.substring(0, 80)}
                </span>
              )}
            </div>

            {/* Attachment icon */}
            {hasAttachments && (
              <div className="flex-shrink-0 pr-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </div>
            )}

            {/* Date */}
            <div className={`w-20 flex-shrink-0 text-right pr-3 py-2 text-xs whitespace-nowrap ${unread ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>
              {formatDate(email.sent_at || email.received_at || email.created_at)}
            </div>
          </div>
        );
      })}
      {loadingMore && (
        <div className="flex items-center justify-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500" />
        </div>
      )}
    </div>
  );
}
